import { useState } from 'react'
import { Pressable } from '@ayble-health/dashboard-components'
import { BarChartIcon, CheckCircleIcon, ThumbUp, ThumbDown } from './icons'
import { RiskDots } from './RiskDots'
import { claimsInsightsFixtures } from '../../mocks/fixtures/careOSPrototype'
import type { ClaimsInsight, ClaimsInsightsState } from '../../models/careOSPrototype'

type FeedbackMap = Record<string, 'up' | 'down' | null>

interface Props {
  state: ClaimsInsightsState
}

// Claims-derived insights in the Patient Summary tab (Claims Insights PRD,
// two-dimension revision). Not a tab of its own, and shown before the first
// visit only: alone, or beneath the onboarding chief complaint, shown the same
// way. Reads straight
// from the local `claimsInsightsFixtures` map — no fetch, no MSW handler.
export function PreVisitClaimsInsights({ state }: Props) {
  const data = claimsInsightsFixtures[state]
  const [feedback, setFeedback] = useState<FeedbackMap>({})
  const [toast, setToast] = useState(false)

  const handleFeedback = (insightId: string, direction: 'up' | 'down') => {
    // Clicking the already-selected direction clears it back to no feedback.
    const isClearing = feedback[insightId] === direction
    setFeedback(prev => ({ ...prev, [insightId]: isClearing ? null : direction }))
    // Feedback is the primary model-training signal and must be
    // confirmed/recognized. A retraction isn't feedback given — no toast.
    if (!isClearing) {
      setToast(true)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Section header — the careOS Blue Grey section-header treatment.
          Bar-chart glyph marks Predict-derived content; it inherits the
          header color. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)',
        color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
      }}>
        <BarChartIcon />
        <span>Pre-initial visit claims data insights</span>
      </div>

      {data.hasClaimsData ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          {data.insights.map(insight => (
            <InsightRow
              key={insight.id}
              insight={insight}
              feedback={feedback[insight.id] ?? null}
              onFeedback={direction => handleFeedback(insight.id, direction)}
            />
          ))}
        </div>
      ) : (
        <p style={{
          margin: 0, padding: '16px 0 24px',
          fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', color: 'var(--blue-grey)',
          fontFamily: 'var(--font-body)',
        }}>
          No claims-based insights are available for this member.
        </p>
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--evergreen)', color: '#fff',
            padding: '10px 16px', borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)', zIndex: 20,
            fontSize: 'var(--text-sm-size)', fontFamily: 'var(--font-body)',
          }}
        >
          <span style={{ color: 'var(--grass)', display: 'inline-flex' }}>
            <CheckCircleIcon size={18} />
          </span>
          Your feedback is confirmed. Thanks for helping us make better predictions.
          <Pressable
            type="button"
            aria-label="Dismiss"
            onClick={() => setToast(false)}
            bg="transparent"
            color="white"
            opacity={0.6}
            ml={1}
            fontSize="md"
            lineHeight={1}
            p={0}
          >
            ×
          </Pressable>
        </div>
      )}
    </div>
  )
}

// One insight: an outlined pill-row of [risk dots] + sentence + inline thumbs.
// Severity reads through the design system's RiskDots (1 green / 2 orange /
// 3 plum), so there's no per-insight icon or headline to maintain. Marked
// "not accurate" (thumbs-down) greys the row's copy — the insight stays
// visible (feedback trains the model, it doesn't curate the display).
function InsightRow({ insight, feedback, onFeedback }: {
  insight: ClaimsInsight
  feedback: 'up' | 'down' | null
  onFeedback: (direction: 'up' | 'down') => void
}) {
  const flagged = feedback === 'down'
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 16px',
      background: 'var(--canvas)',
      border: '1px solid var(--blue-grey)', borderRadius: 12,
      maxWidth: '100%',
    }}>
      {/* Height of one line box, so the dots center on the FIRST line of copy
          rather than on the paragraph as a whole when it wraps. */}
      <span style={{
        flexShrink: 0, display: 'inline-flex', alignItems: 'center',
        height: 'calc(var(--text-sm-size) * var(--text-sm-lh))',
      }}>
        <RiskDots level={insight.severity} showLabel={false} />
      </span>
      <p style={{
        margin: 0, minWidth: 0,
        fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
        color: flagged ? 'var(--steel)' : 'var(--evergreen)', fontFamily: 'var(--font-body)',
      }}>
        {insight.copy}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6, verticalAlign: 'middle' }}>
          {(['up', 'down'] as const).map(direction => {
            const selected = feedback === direction
            const Icon = direction === 'up' ? ThumbUp : ThumbDown
            return (
              <Pressable
                key={direction}
                type="button"
                aria-label={direction === 'up' ? 'Helpful' : 'Not helpful'}
                aria-pressed={selected}
                onClick={() => onFeedback(direction)}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="24px"
                h="24px"
                borderRadius="full"
                flexShrink={0}
                bg={selected ? (direction === 'up' ? 'grass' : 'tomato') : 'transparent'}
                color={selected ? 'white' : 'bluegrey'}
                transition="background 0.15s, color 0.15s"
              >
                <Icon />
              </Pressable>
            )
          })}
        </span>
      </p>
    </div>
  )
}
